import { useMemo } from 'react';

import { BiomassDistributionBin } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { Table, type ColumnType } from '@/components/table';
import { useNumberFormat } from '@/localization';

import type { BiomassDistributionBinSize } from './biomassDistribution.types.ts';
import { getPercentageString, roundAndPad } from './biomassDistribution.utils.ts';

interface BiomassDistributionTableProps {
  /** Contiguous bins from the distribution endpoint, bounds in kg. */
  bins: BiomassDistributionBin[];
  totalCount: number;
  binSize: BiomassDistributionBinSize;
}

type BiomassDistributionRow = {
  weight: string;
  count: string;
  percentage: string;
};

export const BiomassDistributionTable = ({
  bins,
  totalCount,
  binSize,
}: BiomassDistributionTableProps) => {
  const { formatInteger } = useNumberFormat();
  const {
    t,
    i18n: { language },
  } = useTranslation(['orbitOne']);

  const rows = useMemo<BiomassDistributionRow[]>(
    () =>
      // The endpoint includes empty bins so the chart can plot a continuous axis;
      // the table only lists ranges that contain fish.
      bins
        .filter(({ count }) => count > 0)
        .map(({ lowerBound, upperBound, count }) => ({
          weight: t('orbitOne:biomassDistribution.table.weightRange', {
            from: roundAndPad(lowerBound, binSize, language),
            to: roundAndPad(upperBound, binSize, language),
          }),
          count: formatInteger(count),
          percentage: getPercentageString(totalCount, count, language),
        })),
    [bins, totalCount, binSize, formatInteger, language, t]
  );

  const columns = useMemo<ColumnType<BiomassDistributionRow>[]>(
    () => [
      {
        type: 'accessor',
        accessorKey: 'weight',
        header: t('orbitOne:biomassDistribution.table.weight'),
      },
      {
        type: 'accessor',
        accessorKey: 'count',
        header: t('orbitOne:biomassDistribution.table.estimatedCount'),
      },
      {
        type: 'accessor',
        accessorKey: 'percentage',
        header: t('orbitOne:biomassDistribution.table.percentage'),
      },
    ],
    [t]
  );

  return (
    <Table
      columns={columns}
      data={rows}
      state={{}}
      rowCount={rows.length}
      enableSorting={false}
      enableFilters={false}
      align='center'
      manualPagination={false}
      withPagination={false}
    />
  );
};
